import React, { useState, useRef, useEffect } from 'react';

type FilteringInfoItem =
  | { type: 'select' | 'string'; value: string }
  | { type: 'multiselect'; value: string[] };

type FilteringInfo = {
  [filterId: string]: FilteringInfoItem;
};

const ALL_OPTIONS_VALUE = 'All';

interface OptionType {
  value: string;
  label: string;
}

interface MultiSelectFilterProps<T> {
  filter: {
    id: string;
    type: string[] | 'string'; // either 'string' or an array of strings
    multiple?: boolean;
    stringValue: (a: T) => string | null;
    forcedValue?: string;
    onChange?: (value: string[]) => void;
  };
  filterInfoMap: FilteringInfo;
  setFilterInfoMap: React.Dispatch<React.SetStateAction<FilteringInfo>>;
}

const MultiSelectFilter = ({
  filter,
  filterInfoMap,
  setFilterInfoMap
}: MultiSelectFilterProps<any>) => {
  // Convert filter.type into an array of { value, label } objects, including "All."
  const options: OptionType[] = [
    { value: ALL_OPTIONS_VALUE, label: ALL_OPTIONS_VALUE },
    ...(Array.isArray(filter.type)
      ? filter.type.map(opt => ({ value: opt, label: opt }))
      : [])
  ];

  // Get the current array of selected values from the store.
  const selectedValues: string[] =
    filterInfoMap[filter.id]?.type === 'multiselect'
      ? (filterInfoMap[filter.id] as { type: 'multiselect'; value: string[] }).value
      : [];

  // Dropdown open/close state.
  const [isOpen, setIsOpen] = useState(false);

  // Reference to the dropdown wrapper to handle outside clicks.
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside it.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle an item in or out of the selected array.
  const toggleOption = (value: string) => {
    setFilterInfoMap(prev => {
      const oldItem = prev[filter.id] as { type: 'multiselect'; value: string[] } | undefined;
      const oldValues = oldItem?.value || [];

      let newValues: string[];
      if (oldValues.includes(value)) {
        // Remove it if it's already selected
        newValues = oldValues.filter(v => v !== value);
      } else {
        // Otherwise add it
        newValues = [...oldValues, value];
      }

      if (filter.onChange) filter.onChange(newValues);
      return {
        ...prev,
        [filter.id]: { type: 'multiselect', value: newValues }
      };
    });
  };

  // Example button label logic:
  // - If no items selected, show "All" (or "Select...")
  // - If 1-2 items selected, show them
  // - If more, show "X selected"
  let buttonLabel = 'Select...';
  if (selectedValues.length === 0) {
    buttonLabel = ALL_OPTIONS_VALUE;
  } else if (selectedValues.length <= 2) {
    buttonLabel = selectedValues.join(', ');
  } else {
    buttonLabel = `${selectedValues.length} selected`;
  }

  return (
    <div className='MultiSelectFilter-container' ref={wrapperRef}>
      <button
        type="button"
        className='MultiSelectFilter-button'
        onClick={() => setIsOpen(!isOpen)}
      >
        {buttonLabel}
      </button>

      {isOpen && (
        <div className='MultiSelectFilter-dropdown'>
            {options.map(opt => {
            const checked = selectedValues.includes(opt.value);
            return (
                <label
                key={opt.value}
                className='MultiSelectFilter-option'
                >
                <input
                    type="checkbox"
                    checked={checked}
                    className='MultiSelectFilter-checkbox'
                    onChange={() => toggleOption(opt.value)}
                />
                {opt.label}
                </label>
            );
            })}
        </div>
        )}

    </div>
  );
};

export default MultiSelectFilter;
